import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";


function CleanupButton({ onDone }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = () => api.get("/admin/cleanup/preview").then((r) => setP(r.data)).catch(() => toast.error("Aperçu impossible."));
  const run = async () => {
    if (!window.confirm(`Supprimer définitivement ${p.companies} société(s) de test, ${p.users} utilisateur(s) et ${p.establishments} établissement(s) (adresses @example.com / @test.com uniquement) ?`)) return;
    setBusy(true);
    try { const r = await api.post("/admin/cleanup/run"); toast.success(`${r.data.companies} société(s) de test supprimée(s).`); setP(null); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec du nettoyage."); } finally { setBusy(false); }
  };
  return (
    <span className="flex items-center gap-2 text-sm">
      {!p ? <button onClick={preview} className="px-3 h-9 rounded-lg border border-slate-300 hover:bg-slate-50" data-testid="cleanup-preview-btn">Nettoyer les données de test</button>
        : <><span className="text-slate-600">{p.companies} société(s) · {p.users} utilisateur(s) · {p.establishments} établissement(s) de test</span>
          <button onClick={run} disabled={busy || !p.companies} className="px-3 h-9 rounded-lg bg-red-600 text-white disabled:opacity-50" data-testid="cleanup-run-btn">Supprimer</button>
          <button onClick={() => setP(null)} className="text-slate-500" data-testid="cleanup-cancel-btn">Annuler</button></>}
    </span>
  );
}

const d = (s) => (s ? new Date(s).toLocaleDateString("fr-FR") : "—");
const dt = (s) => (s ? new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const TYPES = { real: "Client réel", test: "Test", demo: "Démo", admin: "Admin" };
const TYPE_CLS = { real: "bg-emerald-50 text-emerald-700", test: "bg-amber-50 text-amber-700", demo: "bg-slate-100 text-slate-600", admin: "bg-indigo-50 text-indigo-700" };
const STATUS_CLS = { active: "bg-emerald-50 text-emerald-700", trialing: "bg-blue-50 text-blue-700", cancel_scheduled: "bg-amber-50 text-amber-700", canceled: "bg-slate-100 text-slate-500", past_due: "bg-red-50 text-red-700", unpaid: "bg-red-50 text-red-700" };
const SOURCE_FR = { "respondia.fr": "respondia.fr", direct: "Lien direct", campaign: "Campagne", other: "Autre site" };

export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [internal, setInternal] = useState(true);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [activity, setActivity] = useState([]);

  const load = () => api.get("/admin/analytics/users", { params: { include_internal: internal } }).then((r) => setUsers(r.data));
  useEffect(() => { load(); }, [internal]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (u) => { setSel(u); setActivity((await api.get(`/admin/analytics/users/${u.id}/activity`)).data); };
  const setType = async (u, t) => {
    await api.put(`/admin/analytics/companies/${u.company_id}/account-type`, { account_type: t });
    toast.success("Type de compte mis à jour");
    load();
  };

  const list = users.filter((u) => !q || [u.email, u.establishment, u.company_name].some((x) => (x || "").toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="flex flex-wrap items-center gap-3">
        <Input placeholder="Rechercher e-mail, établissement…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-72" data-testid="users-search" />
        <label className="ml-auto flex items-center gap-2 text-sm text-slate-600"><Switch checked={internal} onCheckedChange={setInternal} data-testid="users-toggle-internal" /> Afficher test / démo / admin</label>
        <CleanupButton onDone={load} />
        <span className="text-xs text-slate-400">{list.length} utilisateur(s)</span>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>E-mail</TableHead><TableHead>Établissement</TableHead><TableHead>Secteur</TableHead><TableHead>Inscription</TableHead><TableHead>Dernière connexion</TableHead>
            <TableHead>Offre</TableHead><TableHead>Statut</TableHead><TableHead>Essai</TableHead><TableHead>Audits</TableHead><TableHead>Dernière activité</TableHead><TableHead>Type</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.map((u) => (
              <TableRow key={u.id} className="cursor-pointer hover:bg-slate-50" onClick={() => open(u)} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium text-sm">{u.email}{u.role === "admin" && <span className="ml-1 text-[10px] text-indigo-600">ADMIN</span>}</TableCell>
                <TableCell className="text-sm text-slate-600">{u.establishment || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{u.sector || "—"}</TableCell>
                <TableCell className="text-xs">{d(u.created_at)}</TableCell>
                <TableCell className="text-xs">{dt(u.last_login)}</TableCell>
                <TableCell className="text-xs uppercase">{u.plan || "—"}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CLS[u.status_code] || "bg-slate-100 text-slate-600"}`}>{u.status}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{u.trial_start ? `${d(u.trial_start)} → ${d(u.trial_end)}` : "—"}</TableCell>
                <TableCell className="text-sm text-center">{u.audits}</TableCell>
                <TableCell className="text-xs">{dt(u.last_activity)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <select value={u.account_type} onChange={(e) => setType(u, e.target.value)} data-testid={`account-type-${u.id}`}
                    className={`text-xs rounded-full px-2 py-0.5 border-0 ${TYPE_CLS[u.account_type]}`}>
                    {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="user-activity-sheet">
          {sel && (
            <>
              <SheetHeader><SheetTitle className="font-heading">{sel.email}</SheetTitle></SheetHeader>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-slate-400">Société</div>{sel.company_name || "—"}</div>
                <div><div className="text-xs text-slate-400">Établissement</div>{sel.establishment || "—"}</div>
                <div><div className="text-xs text-slate-400">Offre / statut</div><span className="uppercase">{sel.plan || "—"}</span> · {sel.status}</div>
                <div><div className="text-xs text-slate-400">Essai</div>{sel.trial_start ? `${d(sel.trial_start)} → ${d(sel.trial_end)}` : "—"}</div>
                <div><div className="text-xs text-slate-400">Inscription</div>{dt(sel.created_at)}</div>
                <div><div className="text-xs text-slate-400">Dernière connexion</div>{dt(sel.last_login)}</div>
                <div className="col-span-2"><div className="text-xs text-slate-400">Fonctionnalités principales</div>{sel.top_features.length ? sel.top_features.join(", ") : "Aucune utilisation enregistrée"}</div>
                <div className="col-span-2"><div className="text-xs text-slate-400">Acquisition</div>
                  {sel.acquisition ? <span>{SOURCE_FR[sel.acquisition.source] || sel.acquisition.source}{sel.acquisition.referrer ? ` · ${sel.acquisition.referrer}` : ""}{sel.acquisition.utm_source ? ` · utm: ${sel.acquisition.utm_source}/${sel.acquisition.utm_medium || "-"}/${sel.acquisition.utm_campaign || "-"}` : ""}{sel.acquisition.first_page ? ` · 1re page ${sel.acquisition.first_page}` : ""}</span> : <span className="text-slate-400">Non mesuré</span>}
                </div>
              </div>
              <h4 className="font-heading font-bold mt-6 mb-2">Historique d'activité</h4>
              {activity.length === 0 && <div className="text-sm text-slate-400">Aucun événement enregistré.</div>}
              <div className="space-y-1" data-testid="user-activity-list">
                {activity.map((e) => (
                  <div key={e.id} className="flex justify-between text-sm py-1.5 border-t border-slate-100"><span>{e.label}</span><span className="text-xs text-slate-400">{dt(e.ts)}</span></div>
                ))}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
